import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { accessKeys } from './keys'
import type { AccessMe } from '~/types/v2/Access'
import { AccessService } from '~/services/api/v2/access/access'
import { useAuthStore } from '~/stores/auth'

// Matches the user store's /users/me poll, so permissions and memberships go
// stale together.
const ACCESS_REFETCH_INTERVAL_MS = 2 * 60 * 1000

// The key and fetch, for callers with no component to hang a query on: route
// middleware reads them through the QueryClient, since a useQuery there would
// add an observer on every navigation that is never cleaned up.
export function accessMeQueryOptions() {
  const accessService = new AccessService()
  const authStore = useAuthStore()

  return {
    queryKey: accessKeys.me(authStore.authServiceId ?? null),
    queryFn: async (): Promise<AccessMe> =>
      unwrapOrThrow(await accessService.getAccessMe()),
  }
}

// The signed-in person's roles and CASL rules. plugins/casl.client.ts holds
// the one observer that keeps the app's ability current; screens ask
// useCan() rather than reading this.
export function useAccessMeQuery(enabled?: MaybeRefOrGetter<boolean>) {
  const queryClient = useQueryClient()
  const authStore = useAuthStore()
  const options = accessMeQueryOptions()

  const query = useQuery({
    ...options,
    // Follows the signed-in person: this observer outlives sign-in, so a key
    // fixed when it was created would stay on whoever was signed in then.
    queryKey: computed(() => accessKeys.me(authStore.authServiceId ?? null)),
    enabled: computed(() => toValue(enabled) ?? true),
    // Refreshes on the /users/me schedule, and on window focus once stale (the
    // client's default), so a role changed by someone else lands without a
    // reload.
    staleTime: ACCESS_REFETCH_INTERVAL_MS,
    refetchInterval: ACCESS_REFETCH_INTERVAL_MS,
  })

  function ensureData() {
    return queryClient.ensureQueryData(accessMeQueryOptions())
  }

  function invalidate() {
    return queryClient.invalidateQueries({
      queryKey: accessMeQueryOptions().queryKey,
    })
  }

  return { ...query, ensureData, invalidate }
}
